import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Mail01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { LanguageSelector } from "@/components/application/language-selector/language-selector";
import { VersionFooter } from "@/components/base/version-footer/version-footer";
import { useMe, getRedirectPath } from "@/hooks/api/use-auth";

interface LegalPageLayoutProps {
    children: React.ReactNode;
}

export function LegalPageLayout({ children }: LegalPageLayoutProps) {
    const { t } = useTranslation(["pages:landing", "pages:legal"]);
    const { data: currentUser } = useMe();
    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== "production";
    const fixedTopBar =
        hasEnvironmentBanner && (env === "development" || env === "staging" || import.meta.env.DEV);

    return (
        <div className={`min-h-screen bg-secondary relative ${hasEnvironmentBanner ? "pt-7" : ""}`}>
            {/* Header */}
            <header
                className={`z-30 border-b border-secondary_alt bg-primary/80 backdrop-blur-sm ${
                    fixedTopBar ? "fixed left-0 right-0 top-7" : "sticky top-0"
                }`}
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex h-16 items-center justify-between gap-2 sm:gap-4">
                        <a href="/" className="min-w-0 shrink">
                            <img
                                src="/images/logo-full.svg"
                                alt="EveMind"
                                className="h-8 w-auto max-w-[min(11rem,46vw)] sm:h-9 sm:max-w-none"
                            />
                        </a>
                        <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
                            <Button
                                href="/contact"
                                size="sm"
                                color="secondary"
                                iconLeading={Mail01}
                                aria-label={t("hero.ctaContact")}
                                className="sm:hidden"
                            />
                            <Button href="/contact" size="sm" color="secondary" className="hidden sm:inline-flex">
                                {t("hero.ctaContact")}
                            </Button>
                            {currentUser ? (
                                <Button href={getRedirectPath(currentUser) ?? "/auth/login"} size="sm" color="primary">
                                    {t("hero.ctaGoToApp")}
                                </Button>
                            ) : (
                                <Button href="/auth/login" size="sm" color="primary">
                                    {t("hero.ctaLogin")}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </header>
            {fixedTopBar ? <div className="h-16 shrink-0" aria-hidden /> : null}

            {/* Main content */}
            <main className="relative z-10 py-12 sm:py-16">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">{children}</div>
            </main>

            {/* Footer */}
            <footer className="border-t border-secondary_alt bg-primary/95 py-10">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col gap-6 border-t border-secondary_alt pt-8 sm:flex-row sm:items-center sm:justify-between">
                        <img src="/images/logo-full.svg" alt="EveMind" className="h-10 w-auto" />
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-quaternary">
                            <Link
                                to="/terms"
                                className="hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 rounded"
                            >
                                {t("footer.terms", { ns: "pages:legal" })}
                            </Link>
                            <span className="text-quaternary/60" aria-hidden>
                                |
                            </span>
                            <Link
                                to="/privacy"
                                className="hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 rounded"
                            >
                                {t("footer.privacy", { ns: "pages:legal" })}
                            </Link>
                        </div>
                        <div className="flex items-center gap-3">
                            <LanguageSelector showLabel={false} />
                            <p className="text-sm text-quaternary">© {new Date().getFullYear()} Eve Mind</p>
                        </div>
                    </div>
                    <div className="mt-4">
                        <VersionFooter />
                    </div>
                </div>
            </footer>
        </div>
    );
}
